/* src/features/5DD/Dashboard.js */

import React, { useState, useEffect } from 'react';
import { db, signOut } from '../../apiClient';
import { logVisit } from '../../analytics';
import { addDays, differenceInDays, startOfDay, setHours, setMinutes } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// --- COMPONENTS ---
import DayView from './DayView';
import Journal from './Journal';
import StartCourse from './StartCourse';
import NutritionView from './NutritionView';
import HistoryView from './HistoryView';
import ArchiveModal from './ArchiveModal';

// --- SERVICES ---
import { calendarService } from '../../services/google/calendar';

const styles = {
  dashboard: {
    width: '100%', maxWidth: '600px', margin: '0 auto',
    padding: '10px', boxSizing: 'border-box',
    borderRadius: '8px',
  },
  controlsRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0 10px 10px 10px', borderBottom: '1px solid #555', marginBottom: '10px',
  },
  homeButton: {
    background: 'transparent', border: '1px solid #fff', color: '#fff',
    padding: '5px 10px', borderRadius: '4px', cursor: 'pointer',
    textDecoration: 'none', fontSize: '1em', fontFamily: 'inherit',
  },
  signOutButton: {
    background: 'transparent', border: '1px solid #fff', color: '#fff',
    padding: '5px 10px', borderRadius: '4px', cursor: 'pointer',
    fontSize: '1em', fontFamily: 'inherit', marginLeft: '10px'
  },
  resetButton: {
    background: '#dc3545', border: '1px solid #dc3545', color: '#fff',
    padding: '5px 10px', borderRadius: '4px', cursor: 'pointer',
    fontSize: '0.8em', marginLeft: '10px', fontFamily: 'inherit',
  },
  primaryNav: {
    display: 'flex', justifyContent: 'center', gap: '10px',
    marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  primaryNavButton: {
    background: 'transparent', border: 'none', color: '#aaa',
    fontSize: '1.1em', cursor: 'pointer', padding: '5px 10px',
    fontWeight: 'bold', borderBottom: '2px solid transparent', transition: 'all 0.2s'
  },
  primaryNavActive: { color: '#fff', borderBottom: '2px solid #b08d6d' },
  navButton: {
    display: 'flex', justifyContent: 'space-around', width: '100%',
    background: 'rgba(0, 0, 0, 0.5)', borderRadius: '8px', padding: '5px', margin: '10px 0',
  },
  navButtonTab: {
    padding: '10px', border: 'none', background: 'transparent',
    fontSize: '1em', fontWeight: 'bold', color: '#eee', cursor: 'pointer',
  },
  navButtonActive: { background: '#fff', borderRadius: '6px', color: '#333' },
  
  // Style for the "Back to Start" button in Inactive History mode
  backToStartBtn: {
    display: 'block', width: '100%', padding: '10px', 
    background: 'rgba(255,255,255,0.1)', border: '1px solid #555', 
    color: '#ddd', borderRadius: '4px', marginBottom: '20px', cursor: 'pointer'
  }
};

function Dashboard({ session, profile }) {
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const [currentView, setCurrentView] = useState('dashboard'); 
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [activeCourseId, setActiveCourseId] = useState(null);
  
  // --- NEW STATE: SIMPLE VIDEO EXECUTION ---
  const [showVideo, setShowVideo] = useState(false);
  
  // --- SYNC ENGINE ---
  const [globalRefresh, setGlobalRefresh] = useState(0);
  const triggerRefresh = () => setGlobalRefresh(prev => prev + 1);

  const navigate = useNavigate();

  // 1. Fetch Active Course ID
  useEffect(() => {
    const fetchActiveCourse = async () => {
        if (!session?.user?.id) return;
        try {
          const data = await db.courses.active();
          if (data?.id) setActiveCourseId(data.id);
        } catch (e) {
          // no active course
        }
    };
    fetchActiveCourse();
  }, [session]);

  // 2. Calculate Day Index
  useEffect(() => {
    if (profile?.course_start_date) {
      const today = startOfDay(new Date());
      const courseStart = startOfDay(new Date(profile.course_start_date));
      const dayIndex = differenceInDays(today, courseStart);
   
      if (dayIndex >= 0 && dayIndex <= 5) setActiveDayIndex(dayIndex);
    }
  }, [profile]);

  // 3. Analytics
  useEffect(() => {
    logVisit('5DD_Dashboard', { userId: session?.user?.id, view: currentView });
  }, [session, currentView]);

  const handleSignOut = () => signOut();

  // --- LOGIC: Active vs Inactive Mode ---
  const hasActiveCourse = !!profile?.course_start_date;

  // --- CALENDAR LOGIC ---
  const populateCourseCalendar = async (token, startDate) => {
    if (!token) {
      console.warn('[Calendar] populateCourseCalendar called with no token — aborting');
      return;
    }
    console.log('[Calendar] Creating 16 events starting from', startDate.toISOString());

    const createHH5Event = (dateObj, title) => {
      const endObj = new Date(dateObj.getTime() + 5 * 60000);
      return {
        summary: title,
        location: '/hh5',
        description: 'Start your practice: /hh5',
        start: { dateTime: dateObj.toISOString() },
        end: { dateTime: endObj.toISOString() },
        reminders: {
          useDefault: false,
          overrides: [{ method: 'popup', minutes: 5 }]
        }
      };
    };

    const promises = [];
    // Day 0: Tonight
    promises.push(calendarService.createEvent(token, createHH5Event(startDate, 'HH5 - Night Practice')));
    // Days 1-5
    for (let i = 1; i <= 5; i++) {
      const dayBase = addDays(startDate, i);
      const morning = setMinutes(setHours(dayBase, 8), 20);
      promises.push(calendarService.createEvent(token, createHH5Event(morning, 'HH5 - Morning Practice')));
      const afternoon = setMinutes(setHours(dayBase, 16), 20);
      promises.push(calendarService.createEvent(token, createHH5Event(afternoon, 'HH5 - Afternoon Practice')));
      const evening = setMinutes(setHours(dayBase, 20), 20);
      promises.push(calendarService.createEvent(token, createHH5Event(evening, 'HH5 - Night Practice')));
    }

    const results = await Promise.allSettled(promises);
    const fulfilled = results.filter(r => r.status === 'fulfilled').length;
    const rejected = results.filter(r => r.status === 'rejected');
    console.log(`[Calendar] Events created: ${fulfilled}/16 succeeded`);
    if (rejected.length > 0) {
      rejected.forEach((r) => console.error('[Calendar] Event failed:', r.reason?.message || r.reason));
    }
  };

  // --- HANDLERS ---
  const handleStartCourse = async () => {
    const today = new Date();
    const courseStartDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 20, 20, 0);

    // Try session token first, fall back to localStorage
    const googleToken = session?.provider_token || localStorage.getItem('google_provider_token');
    console.log('[StartCourse] session.provider_token:', session?.provider_token ? 'PRESENT' : 'null');
    console.log('[StartCourse] localStorage google_provider_token:', localStorage.getItem('google_provider_token') ? 'PRESENT' : 'null');
    console.log('[StartCourse] Using token:', googleToken ? 'PRESENT' : 'NONE — calendar events will be skipped');

    // Run calendar population and profile update in parallel, but wait for both before reloading
    const calendarPromise = googleToken
      ? populateCourseCalendar(googleToken, courseStartDate).catch(err =>
          console.error('[StartCourse] Calendar population error:', err)
        )
      : (console.warn('[StartCourse] No Google token available — skipping calendar event creation'), Promise.resolve());

    const profilePromise = db.profiles.update({ course_start_date: courseStartDate.toISOString() });

    const [, profileResult] = await Promise.all([calendarPromise, profilePromise.catch(e => { console.error('[StartCourse] Error updating profile:', e.message); return null; })]);

    console.log('[StartCourse] Done — reloading page');
    window.location.reload();
  };

  const handleConfirmArchive = async (note) => {
    try {
        if (activeCourseId) {
             await db.courses.update(activeCourseId, {
                 is_active: false,
                 status: 'archived',
                 end_date: new Date().toISOString(),
                 user_note: note,
                 end_weight: profile?.weight
             });
        }

        await db.profiles.update({ course_start_date: null });

        setIsArchiveModalOpen(false);
        window.location.reload();

    } catch (error) {
        console.error("Archive failed:", error);
        alert(`Archive Failed: ${error.message}`);
    }
  };

  // --- RENDER LOGIC ---

  // 1. If NO active course AND NOT viewing history -> Show Start Screen
  if (!hasActiveCourse && currentView !== 'history') {
    return (
        <StartCourse 
            onStart={handleStartCourse} 
            onViewHistory={() => setCurrentView('history')}
        />
    );
  }

  // 2. Main Dashboard (Active or History View)
  const courseStartDate = hasActiveCourse ? new Date(profile.course_start_date) : new Date();
  const days = Array.from({ length: 6 }, (_, i) => addDays(courseStartDate, i));
  const activeDay = days[activeDayIndex];
  
  const getNavStyle = (viewName) => ({
    ...styles.primaryNavButton,
    ...(currentView === viewName ? styles.primaryNavActive : {})
  });

  return (
    <div style={styles.dashboard}>
      
      {/* HEADER */}
      <div style={styles.controlsRow}>
        <button onClick={() => navigate('/home')} style={styles.homeButton}>&larr; Home</button>
        <div>
          {hasActiveCourse && (
             <button onClick={() => setIsArchiveModalOpen(true)} style={styles.resetButton}>Reset / Finish</button>
          )}
          <button onClick={handleSignOut} style={styles.signOutButton}>Sign Out</button>
        </div>
      </div>
      
      {/* TABS (Only show if Active Course) */}
      {hasActiveCourse && (
        <div style={styles.primaryNav}>
            <button style={getNavStyle('dashboard')} onClick={() => setCurrentView('dashboard')}>Tasks</button>
            <button style={getNavStyle('journal')} onClick={() => setCurrentView('journal')}>Journal</button>
            <button style={getNavStyle('nutrition')} onClick={() => setCurrentView('nutrition')}>Nutrition</button>
            <button style={getNavStyle('history')} onClick={() => setCurrentView('history')}>History</button>
        </div>
      )}

      {/* "Back to Start" (Only show if Inactive Mode & Viewing History) */}
      {!hasActiveCourse && currentView === 'history' && (
          <button style={styles.backToStartBtn} onClick={() => setCurrentView('dashboard')}>
              &larr; Start New Course
          </button>
      )}

      {/* DAY NAV (Only for Active Tasks & Journal) */}
      {hasActiveCourse && (currentView === 'dashboard' || currentView === 'journal') && (
          <nav style={styles.navButton}>
            {days.map((day, index) => {
              if (index === 0) return null; 
              return (
                <button
                  key={index}
                  style={{ ...styles.navButtonTab, ...(index === activeDayIndex ? styles.navButtonActive : {}) }}
                  onClick={() => setActiveDayIndex(index)}
                >
                  Day {index}
                </button>
              );
            })}
          </nav>
      )}
  
      {/* VIEWS */}
      {currentView === 'dashboard' ? (
        <>
          {/* --- SIMPLE VIDEO EXECUTION --- */}
          <div style={{ margin: '15px 0' }}>
            {!showVideo ? (
              <button 
                onClick={() => setShowVideo(true)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  width: '100%', padding: '12px', backgroundColor: 'rgba(255,255,255,0.05)', 
                  border: '1px solid #555', borderRadius: '8px', color: '#fff', 
                  cursor: 'pointer', textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.85em'
                }}
              >
                <svg viewBox="0 0 24 24" style={{ width: '16px', height: '16px', fill: 'currentColor' }}>
                  <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"></path>
                </svg>
                How to do the Five Days Different
              </button>
            ) : (
              <div style={{ backgroundColor: '#111', padding: '10px', borderRadius: '8px', border: '1px solid #444' }}>
                <div style={{ display: 'flex', justifyContent: 'space-betweena', alignItems: 'center', marginBottom: '10px', padding: '0 5px' }}>
                  <span style={{ color: '#ccc', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '1px' }}>Orientation Video</span>
                  <button onClick={() => setShowVideo(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>
                    ✕ CLOSE
                  </button>
                </div>
                <video 
                  src="/server_assets/5DD_intro_cosmo.mp4" 
                  controls 
                  autoPlay 
                  playsInline
                  style={{ width: '100%', borderRadius: '4px', backgroundColor: '#000' }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>
          {/* ------------------------------ */}

          <DayView
            key={`day-${activeDayIndex}-${globalRefresh}`} 
            session={session}
            day={activeDay}
            dayIndex={activeDayIndex}
            navigateTo={setCurrentView}
            onUpdate={triggerRefresh} 
          />
        </>
      ) : currentView === 'journal' ? (
        <Journal
          key={`journal-${activeDayIndex}`}
          session={session}
          day={activeDay}
          dayIndex={activeDayIndex}
          navigateTo={setCurrentView}
        />
      ) : currentView === 'nutrition' ? (
        <NutritionView 
          session={session} 
          profile={profile}
          activeCourseId={activeCourseId} 
          refreshTrigger={globalRefresh}  
          onDataUpdate={triggerRefresh}   
        />
      ) : (
        <HistoryView 
          session={session} 
          profile={profile} 
        />
      )}

      {hasActiveCourse && (
          <ArchiveModal 
            isOpen={isArchiveModalOpen}
            onClose={() => setIsArchiveModalOpen(false)}
            onConfirm={handleConfirmArchive}
            session={session}
            activeCourseId={activeCourseId}
        />
      )}

    </div>
  );
}

export default Dashboard;
